/**
 * All camera plumbing for the Anear viewfinder, with no DOM of its own.
 *
 * Kept free of React and of the custom elements so the negotiation logic (which is the
 * part that actually differs across iOS, Android, and desktop) can be tested against
 * mocked `navigator.mediaDevices` rather than a real camera.
 */

import {
  BACK,
  FRONT,
  buildLensChips,
  canFlipFacing,
  defaultLensFor,
  groupCameraDevices
} from './cameraLenses'
import { coverCrop } from './cameraCrop'

export { FRONT, BACK }

export const CAMERA_ERROR_DENIED = 'denied'
export const CAMERA_ERROR_NOT_FOUND = 'not-found'
export const CAMERA_ERROR_IN_USE = 'in-use'
export const CAMERA_ERROR_UNSUPPORTED = 'unsupported'
export const CAMERA_ERROR_UNKNOWN = 'unknown'

export class CameraError extends Error {
  constructor(kind, message, cause = null) {
    super(message)
    this.name = 'CameraError'
    this.kind = kind
    this.cause = cause
  }
}

export function classifyCameraError(error) {
  const name = error?.name || ''
  if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
    return CAMERA_ERROR_DENIED
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') return CAMERA_ERROR_NOT_FOUND
  if (name === 'NotReadableError' || name === 'TrackStartError') return CAMERA_ERROR_IN_USE
  return CAMERA_ERROR_UNKNOWN
}

export function cameraErrorMessage(kind, { allowLibraryFallback = false } = {}) {
  switch (kind) {
    case CAMERA_ERROR_DENIED:
      return allowLibraryFallback
        ? 'Camera access was blocked. Enable it in your browser settings, or choose a photo from your library.'
        : 'Camera access was blocked. Enable it in your browser settings and try again.'
    case CAMERA_ERROR_NOT_FOUND:
      return allowLibraryFallback
        ? 'No camera was found on this device. Choose a photo from your library instead.'
        : 'No camera was found on this device.'
    case CAMERA_ERROR_IN_USE:
      return 'Another app is using the camera. Close it and try again.'
    case CAMERA_ERROR_UNSUPPORTED:
      return allowLibraryFallback
        ? 'Camera capture is not supported on this browser. Choose a photo from your library instead.'
        : 'Camera capture is not supported on this browser.'
    default:
      return 'Could not start the camera.'
  }
}

/**
 * Ordered constraint attempts. Always lead with the caller's ideal (4K by default) so
 * the browser picks the highest mode it can; dimensions stay `ideal` so a weaker sensor
 * degrades instead of rejecting. Only `deviceId` is ever `exact`.
 */
export function buildConstraintLadder({
  deviceId = '',
  facingMode = FRONT,
  audio = false,
  idealWidth = 2160,
  idealHeight = 3840
} = {}) {
  const rungs = []
  const fallbackWidth = Math.min(idealWidth, 1080)
  const fallbackHeight = Math.min(idealHeight, 1920)
  const hiRes = {
    width: { ideal: idealWidth },
    height: { ideal: idealHeight },
    aspectRatio: { ideal: 9 / 16 },
    frameRate: { ideal: 30 }
  }
  const midRes = {
    width: { ideal: fallbackWidth },
    height: { ideal: fallbackHeight },
    aspectRatio: { ideal: 9 / 16 },
    frameRate: { ideal: 30 }
  }

  if (deviceId) {
    rungs.push({ deviceId: { exact: deviceId }, ...hiRes })
    rungs.push({ deviceId: { exact: deviceId }, ...midRes })
    rungs.push({ deviceId: { exact: deviceId } })
  }

  rungs.push({ facingMode, ...hiRes })
  rungs.push({ facingMode, ...midRes })
  rungs.push({ facingMode })

  return rungs.map((video) => ({ video, audio }))
}

function supportedConstraints() {
  try {
    return navigator.mediaDevices?.getSupportedConstraints?.() || {}
  } catch {
    return {}
  }
}

/**
 * Normalize `getCapabilities()` into a shape the UI can gate on directly. Absent controls
 * come back null/false so a viewfinder can simply not render them.
 */
export function readTrackCapabilities(track) {
  let caps = {}
  let settings = {}

  try {
    caps = (typeof track?.getCapabilities === 'function' && track.getCapabilities()) || {}
  } catch {
    caps = {}
  }
  try {
    settings = (typeof track?.getSettings === 'function' && track.getSettings()) || {}
  } catch {
    settings = {}
  }

  const zoomRange = caps.zoom
  const zoom =
    zoomRange && Number.isFinite(zoomRange.max) && Number.isFinite(zoomRange.min) && zoomRange.max > zoomRange.min
      ? {
          min: zoomRange.min,
          max: zoomRange.max,
          step: Number.isFinite(zoomRange.step) && zoomRange.step > 0 ? zoomRange.step : 0.1,
          current: Number.isFinite(settings.zoom) ? settings.zoom : zoomRange.min
        }
      : null

  const torch = caps.torch === true || (Array.isArray(caps.torch) && caps.torch.includes(true))

  const focusModes = Array.isArray(caps.focusMode) ? caps.focusMode : []
  const supported = supportedConstraints()

  return {
    zoom,
    torch,
    focusModes,
    // Tap-to-focus needs both a settable focus mode and a point to aim it at. This is
    // Chrome-on-Android in practice; iOS Safari exposes neither.
    tapToFocus: Boolean(supported.pointsOfInterest) && focusModes.some((m) => m === 'single-shot' || m === 'manual'),
    width: Number(settings.width) || 0,
    height: Number(settings.height) || 0,
    frameRate: Number(settings.frameRate) || 0,
    facingMode: settings.facingMode || '',
    deviceId: settings.deviceId || ''
  }
}

function centerCrop(width, height, zoom) {
  return coverCrop(width, height, { zoom })
}

function canvasToBlob(canvas, mimeType, quality) {
  return new Promise((resolve, reject) => {
    if (typeof canvas.toBlob !== 'function') {
      reject(new Error('Canvas capture is not supported on this browser.'))
      return
    }
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not capture photo.'))),
      mimeType,
      quality
    )
  })
}

async function decodeBlob(blob) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob)
      return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close?.() }
    } catch {
      // fall through to the <img> decode
    }
  }

  const url = URL.createObjectURL(blob)
  try {
    const img = await new Promise((resolve, reject) => {
      const element = new Image()
      element.onload = () => resolve(element)
      element.onerror = () => reject(new Error('Could not read the captured photo.'))
      element.src = url
    })
    return {
      source: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      release: () => URL.revokeObjectURL(url)
    }
  } catch (error) {
    URL.revokeObjectURL(url)
    throw error
  }
}

/**
 * Draw a decoded frame out to a Blob, applying mirroring and digital zoom in one pass.
 *
 * Output pixel dimensions are the cropped source dimensions, so digital zoom trades field
 * of view for resolution exactly like a native camera does, rather than upscaling.
 */
export async function renderStill({
  source,
  width,
  height,
  mirror = false,
  digitalZoom = 1,
  mimeType = 'image/jpeg',
  quality = 0.95
}) {
  const { sx, sy, sw, sh } = centerCrop(width, height, digitalZoom)
  const outWidth = Math.max(1, Math.round(sw))
  const outHeight = Math.max(1, Math.round(sh))

  const canvas = document.createElement('canvas')
  canvas.width = outWidth
  canvas.height = outHeight

  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Could not capture photo.')

  if (mirror) {
    ctx.translate(outWidth, 0)
    ctx.scale(-1, 1)
  }
  ctx.drawImage(source, sx, sy, sw, sh, 0, 0, outWidth, outHeight)

  return canvasToBlob(canvas, mimeType, quality)
}

export default class CameraEngine {
  constructor({ audio = false } = {}) {
    this._audio = !!audio
    this._stream = null
    this._track = null
    this._capabilities = null
    this._lenses = { all: [], front: [], back: [], unknown: [] }
    this._chips = { [FRONT]: [], [BACK]: [] }
    this._activeDeviceId = ''
    this._activeFacing = FRONT
    this._imageCapture = null
  }

  static isSupported() {
    return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
  }

  get stream() {
    return this._stream
  }

  get track() {
    return this._track
  }

  get capabilities() {
    return this._capabilities
  }

  get facingMode() {
    return this._activeFacing
  }

  get deviceId() {
    return this._activeDeviceId
  }

  get lenses() {
    return this._lenses
  }

  chipsFor(facing) {
    return this._chips[facing] || []
  }

  get canFlip() {
    return canFlipFacing(this._lenses)
  }

  /**
   * Acquire a stream, walking the constraint ladder. A denied or busy camera aborts
   * immediately; everything else falls through to the next rung.
   */
  async open({ facingMode = FRONT, deviceId = '', idealWidth, idealHeight } = {}) {
    if (!CameraEngine.isSupported()) {
      throw new CameraError(CAMERA_ERROR_UNSUPPORTED, cameraErrorMessage(CAMERA_ERROR_UNSUPPORTED))
    }

    const ladder = buildConstraintLadder({
      deviceId,
      facingMode,
      audio: this._audio,
      idealWidth,
      idealHeight
    })

    let lastError = null
    for (const constraints of ladder) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints)
        const rungDeviceId = constraints.video?.deviceId?.exact || ''
        this._adoptStream(stream, facingMode, rungDeviceId)
        return this._capabilities
      } catch (error) {
        lastError = error
        const kind = classifyCameraError(error)
        if (kind === CAMERA_ERROR_DENIED || kind === CAMERA_ERROR_IN_USE) {
          throw new CameraError(kind, cameraErrorMessage(kind), error)
        }
      }
    }

    const kind = classifyCameraError(lastError)
    throw new CameraError(kind, cameraErrorMessage(kind), lastError)
  }

  _adoptStream(stream, requestedFacing, requestedDeviceId = '') {
    this.stopStream()
    this._stream = stream
    this._track = stream.getVideoTracks()[0] || null
    this._capabilities = readTrackCapabilities(this._track)
    // iOS Safari often reports facingMode "user" for the rear camera, or leaves
    // it blank. Trusting that report dropped the 0.5/1/3x chips and the next
    // reopen asked for the selfie camera. The constraint we sent is the camera
    // the guest selected.
    this._activeFacing = requestedFacing || this._capabilities.facingMode || FRONT
    this._activeDeviceId = requestedDeviceId || this._capabilities.deviceId || ''
    this._imageCapture = null
  }

  /**
   * Enumerate lenses. Labels are only populated once permission has been granted, so this
   * is worth calling again after the first successful `open()`.
   */
  async refreshLenses() {
    if (!navigator.mediaDevices?.enumerateDevices) return this._lenses

    let devices = []
    try {
      devices = await navigator.mediaDevices.enumerateDevices()
    } catch {
      return this._lenses
    }

    this._lenses = groupCameraDevices(devices)
    this._chips = {
      [FRONT]: buildLensChips(this._lenses.front),
      [BACK]: buildLensChips(this._lenses.back)
    }
    return this._lenses
  }

  defaultLensFor(facing) {
    const list = facing === BACK ? this._lenses.back : this._lenses.front
    return defaultLensFor(list)
  }

  async switchTo({ facingMode, deviceId = '', idealWidth, idealHeight } = {}) {
    const facing = facingMode || this._activeFacing
    return this.open({ facingMode: facing, deviceId, idealWidth, idealHeight })
  }

  supports(control) {
    const caps = this._capabilities
    if (!caps) return false
    if (control === 'zoom') return !!caps.zoom
    if (control === 'torch') return !!caps.torch
    if (control === 'tapToFocus') return !!caps.tapToFocus
    return false
  }

  /**
   * Some implementations only honor constrainable properties inside `advanced`, others
   * only at the top level, so try both before giving up.
   */
  async _applyConstraint(key, value) {
    if (!this._track?.applyConstraints) return false
    try {
      await this._track.applyConstraints({ advanced: [{ [key]: value }] })
      return true
    } catch {
      try {
        await this._track.applyConstraints({ [key]: value })
        return true
      } catch {
        return false
      }
    }
  }

  async setZoom(value) {
    const zoom = this._capabilities?.zoom
    if (!zoom) return false

    const clamped = Math.min(zoom.max, Math.max(zoom.min, Number(value) || zoom.min))
    const applied = await this._applyConstraint('zoom', clamped)
    if (applied) this._capabilities = { ...this._capabilities, zoom: { ...zoom, current: clamped } }
    return applied
  }

  async setTorch(on) {
    if (!this._capabilities?.torch) return false
    return this._applyConstraint('torch', !!on)
  }

  /**
   * @param {number} x normalized 0..1 across the frame
   * @param {number} y normalized 0..1 down the frame
   */
  async focusAt(x, y) {
    if (!this._capabilities?.tapToFocus || !this._track?.applyConstraints) return false

    const point = {
      x: Math.min(1, Math.max(0, Number(x) || 0)),
      y: Math.min(1, Math.max(0, Number(y) || 0))
    }
    const mode = this._capabilities.focusModes.includes('single-shot') ? 'single-shot' : 'manual'

    try {
      await this._track.applyConstraints({
        advanced: [{ focusMode: mode, pointsOfInterest: [point] }]
      })
      return true
    } catch {
      return false
    }
  }

  _ensureImageCapture() {
    if (this._imageCapture) return this._imageCapture
    if (typeof window === 'undefined' || typeof window.ImageCapture !== 'function') return null
    if (!this._track) return null

    try {
      this._imageCapture = new window.ImageCapture(this._track)
    } catch {
      this._imageCapture = null
    }
    return this._imageCapture
  }

  /**
   * Still capture, best path first for guest WYSIWYG:
   *
   *   1. Flash / fill-light stills still use `ImageCapture.takePhoto()` (only path that
   *      honors fillLightMode).
   *   2. `ImageCapture.grabFrame()` — current track frame, matches the live preview.
   *   3. `drawImage()` from the preview `<video>` element.
   *   4. `ImageCapture.takePhoto()` at sensor maxima — last resort. On Chrome desktop this
   *      path is slow and can return a buffered/stale still that is not what the guest saw.
   *
   * Paths 2–4 produce an unmirrored frame; `mirror` is applied uniformly. Path 4 skips the
   * canvas round-trip when no transform is needed.
   */
  async capturePhoto({ videoElement = null, mirror = false, digitalZoom = 1, fillLight = null } = {}) {
    const needsTransform = mirror || digitalZoom > 1
    const imageCapture = this._ensureImageCapture()

    if (fillLight && imageCapture?.takePhoto) {
      const flashed = await this._takePhotoBlob(imageCapture, { fillLight, mirror, digitalZoom, needsTransform })
      if (flashed) return flashed
    }

    if (imageCapture?.grabFrame) {
      try {
        const bitmap = await imageCapture.grabFrame()
        if (bitmap?.width) {
          const blob = await renderStill({
            source: bitmap,
            width: bitmap.width,
            height: bitmap.height,
            mirror,
            digitalZoom
          })
          bitmap.close?.()
          return blob
        }
      } catch {
        // fall through to the preview grab
      }
    }

    const width = videoElement?.videoWidth || 0
    const height = videoElement?.videoHeight || 0
    if (width && height) {
      return renderStill({ source: videoElement, width, height, mirror, digitalZoom })
    }

    if (imageCapture?.takePhoto) {
      const still = await this._takePhotoBlob(imageCapture, {
        fillLight,
        mirror,
        digitalZoom,
        needsTransform
      })
      if (still) return still
    }

    throw new CameraError(CAMERA_ERROR_UNKNOWN, 'Camera is not ready yet. Try again.')
  }

  async _takePhotoBlob(imageCapture, { fillLight, mirror, digitalZoom, needsTransform }) {
    try {
      const settings = await this._photoSettings(imageCapture, fillLight)
      const blob = await imageCapture.takePhoto(settings)
      if (!blob?.size) return null
      if (!needsTransform) return blob
      return this._transformBlob(blob, { mirror, digitalZoom })
    } catch {
      return null
    }
  }

  async _photoSettings(imageCapture, fillLight) {
    if (typeof imageCapture.getPhotoCapabilities !== 'function') return undefined

    try {
      const caps = await imageCapture.getPhotoCapabilities()
      const settings = {}

      const maxWidth = caps?.imageWidth?.max
      const maxHeight = caps?.imageHeight?.max
      if (Number.isFinite(maxWidth)) settings.imageWidth = maxWidth
      if (Number.isFinite(maxHeight)) settings.imageHeight = maxHeight

      if (fillLight && Array.isArray(caps?.fillLightMode) && caps.fillLightMode.includes(fillLight)) {
        settings.fillLightMode = fillLight
      }

      return Object.keys(settings).length ? settings : undefined
    } catch {
      return undefined
    }
  }

  async _transformBlob(blob, { mirror, digitalZoom }) {
    const decoded = await decodeBlob(blob)
    try {
      return await renderStill({
        source: decoded.source,
        width: decoded.width,
        height: decoded.height,
        mirror,
        digitalZoom,
        mimeType: blob.type === 'image/png' ? 'image/png' : 'image/jpeg'
      })
    } finally {
      decoded.release?.()
    }
  }

  stopStream() {
    if (this._stream) {
      this._stream.getTracks().forEach((track) => {
        try {
          track.stop()
        } catch {
          // a track that already ended is not an error worth surfacing
        }
      })
    }
    this._stream = null
    this._track = null
    this._imageCapture = null
  }

  close() {
    this.stopStream()
    this._capabilities = null
  }
}
